
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Quest } from '@/types/quests';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { FormValues } from '../schema/QuestFormSchema';

interface UseQuestFormProps {
  quest: Quest | null;
  onSave: () => void;
}

export const useQuestForm = ({ quest, onSave }: UseQuestFormProps) => {
  const { t } = useTranslation(['admin', 'common']);
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (values: FormValues) => {
    try {
      setSaving(true);
      setFormError(null);
      console.log('Submitting quest data:', values);
      
      const questData = {
        name: values.name,
        description: values.description,
        points_awarded: values.points_awarded,
        type: values.type,
        badge_id: values.badge_id === "null" ? null : values.badge_id || null,
        is_active: values.is_active,
        end_date: values.end_date || null,
      };
      
      console.log('Formatted quest data:', questData);
      
      if (quest) {
        console.log('Updating existing quest:', quest.id);
        const { data, error } = await supabase
          .from('quests')
          .update(questData)
          .eq('id', quest.id)
          .select();
          
        if (error) {
          console.error('Supabase update error:', error);
          throw error;
        }
        
        console.log('Update response data:', data);
      } else {
        console.log('Creating new quest');
        const { data, error } = await supabase
          .from('quests')
          .insert({
            ...questData,
            start_date: new Date().toISOString(),
          })
          .select();
          
        if (error) {
          console.error('Supabase insert error:', error);
          throw error;
        }
        
        console.log('Insert response data:', data);
      }
      
      onSave();
      toast({
        title: t('admin:success'),
        description: t('admin:questSavedSuccess'),
      });
    } catch (error: any) {
      console.error('Error saving quest:', error);
      let errorMessage = error.message || t('admin:errorSavingQuest');
      
      // Check for RLS policy violation
      if (error.message?.includes('row-level security') || error.message?.includes('violates row')) {
        errorMessage = t('admin:rlsErrorMessage', 'Permission denied: You need administrator rights to manage quests');
      }
      
      setFormError(errorMessage);
      toast({
        title: t('common:error'),
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    formError,
    handleSubmit
  };
};
